// VRBB rug calculator — custom size pricing and the draft-order handoff.
//
// Page structure, forms and pill ordering live in vrbb-product.js. This file
// owns the size dropdowns, the price, and the order button.
//
// BANDS
// Rate and freight are both chosen by the rug's WIDTH, from two independent
// tables emitted in #vrbb-bands-data. A weaver no longer has one rate. Max
// width is derived from the highest rate band rather than read from anywhere,
// so the dropdown and the price can never disagree about the ceiling.
//
// Freight is flat dollars, never per square foot, and never discounted: the
// trade discount applies to the rug only. Folding freight into the rate would
// have discounted it AND charged the wrong amount at every size.
//
// CUSTOM_CHECKOUT_ENABLED:
//   true  -> "Order this size" POSTs to the app proxy and redirects to invoice_url
//   false -> falls back to the pre-filled contact form in vrbb-calculator.liquid

(function () {
  'use strict';

  var CUSTOM_CHECKOUT_ENABLED = true;
  var DRAFT_ORDER_ENDPOINT = '/apps/vrbb/draft-order';
  var ORDER_LABEL = 'Order this size';
  var OVER = 'over';

  // ---------- utils ----------
  function calculatePrice(rate, widthIn, lengthIn) {
    return Math.ceil(rate * (widthIn / 12) * (lengthIn / 12) / 5) * 5;
  }
  function tradePrice(basePrice, tier) {
    if (tier === 'vip') return basePrice * 0.75;
    if (tier === 'trade') return basePrice * 0.80;
    return basePrice;
  }
  function toInches(feet, inches) {
    return parseInt(feet, 10) * 12 + parseInt(inches, 10);
  }
  function formatPrice(amount) {
    var hasCents = Math.round(amount * 100) % 100 !== 0;
    return '$' + amount.toLocaleString('en-US', {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2
    });
  }
  function formatDimension(totalInches) {
    var feet = Math.floor(totalInches / 12);
    var inches = totalInches % 12;
    return feet + '\u2032' + inches + '\u2033';
  }
  function formatCeiling(totalInches) {
    var feet = Math.floor(totalInches / 12);
    var inches = totalInches % 12;
    return feet + '\u2032' + (inches ? inches + '\u2033' : '');
  }
  function setText(id, txt) { var el = document.getElementById(id); if (el) el.textContent = txt; }
  function setVal(id, v) { var el = document.getElementById(id); if (el) el.value = v; }
  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }

  // ---------- data ----------
  var dataEl = document.getElementById('vrbb-calculator-data');
  if (!dataEl) return;

  // Band tables for this weaver. Widths are whole inches, f and t inclusive.
  // No decimal feet anywhere: 8.92 and 8.91 are one inch apart and one typo
  // apart, which is why band edges are stored as inches everywhere.
  var bands = { rate: [], freight: [] };
  var bandsEl = document.getElementById('vrbb-bands-data');
  if (bandsEl) {
    try {
      bands = JSON.parse(bandsEl.textContent) || bands;
    } catch (e) {
      // One text-typed metaobject field breaks the whole tag, and the calculator
      // would then simply not appear with nothing to explain why. Say so.
      console.error('VRBB: band data is not valid JSON — the calculator cannot price this rug.', e);
    }
  }

  function bandValue(list, widthIn) {
    for (var i = 0; i < list.length; i++) {
      if (widthIn >= list[i].f && widthIn <= list[i].t) return list[i].v;
    }
    return null; // no band covers this width — not a fallback, an answer
  }
  function rateAt(widthIn) { return bandValue(bands.rate, widthIn); }
  function freightAt(widthIn) { return bandValue(bands.freight, widthIn); }

  // Derived, never stored. One source of truth for the ceiling.
  //
  // It is the LOWER of the two tables' tops, not the rate table's. A width with
  // a rate but no freight band cannot be quoted -- showPrice refuses it -- so
  // offering it in the dropdown produces an option that dead-ends in the quote
  // form. Today the two tops match on every weaver; they stop matching the
  // moment a rate band is added without its freight counterpart, and this is
  // what keeps that from reaching a shopper.
  //
  // The same rule is in vrbb-calculator.liquid, as max_offer_in. Neither copy
  // can be removed: that one filters the shortcut buttons, which are rendered
  // server-side and never reach this script if they were not emitted; this one
  // builds the width dropdown, which Liquid never sees. Two questions, one
  // source -- both derive from the band tables below, so they can only disagree
  // if someone edits one language and not the other, which shows in a diff.
  //
  // Do not replace this with a data attribute emitted by the Liquid. The script
  // would then hold both the JSON and a number claiming to summarise it, and
  // would believe the number. That is the shape of the over-ceiling defect.
  function topOf(list) {
    var top = 0;
    for (var i = 0; i < list.length; i++) if (list[i].t > top) top = list[i].t;
    return top;
  }
  var rateTop = topOf(bands.rate);
  var freightTop = topOf(bands.freight);
  var maxWidthIn = Math.min(rateTop, freightTop);
  if (rateTop !== freightTop) {
    console.error(
      'VRBB: rate bands reach ' + rateTop + 'in but freight bands reach ' +
      freightTop + 'in. Widths above ' + maxWidthIn + 'in are not offered.'
    );
  }
  var maxLengthIn = parseInt(dataEl.dataset.maxLengthIn, 10) || 0;

  // Nothing to price against. Leave the page in its empty state rather than
  // offering dropdowns that cannot produce a number.
  if (!bands.rate.length || !bands.freight.length || !maxWidthIn || !maxLengthIn) return;
  var maxWidthFt = Math.floor(maxWidthIn / 12);
  var maxLengthFt = Math.floor(maxLengthIn / 12);
  var isTrade = dataEl.dataset.isTrade === 'true';
  var tradeTier = dataEl.dataset.tradeTier;

  var inStockSizes = [];
  var instockEl = document.getElementById('vrbb-instock-data');
  if (instockEl) {
    try { inStockSizes = JSON.parse(instockEl.textContent) || []; } catch (e) { inStockSizes = []; }
  }
  function matchInStock(widthIn, lengthIn) {
    for (var i = 0; i < inStockSizes.length; i++) {
      var s = inStockSizes[i];
      var pw = Math.round(s.w * 12);
      var pl = Math.round(s.l * 12);
      if ((pw === widthIn && pl === lengthIn) || (pw === lengthIn && pl === widthIn)) return s;
    }
    return null;
  }

  // ---------- elements ----------
  var widthFtSel = document.getElementById('vrbb-width-ft');
  var widthInSel = document.getElementById('vrbb-width-in');
  var lengthFtSel = document.getElementById('vrbb-length-ft');
  var lengthInSel = document.getElementById('vrbb-length-in');

  var emptyEl = document.getElementById('vrbb-empty');
  var guardEl = document.getElementById('vrbb-guard');
  var guardSwitchBtn = document.getElementById('vrbb-guard-switch');
  var summaryEl = document.getElementById('vrbb-summary');
  var shipEl = document.getElementById('vrbb-summary-ship');
  var freightWrap = document.getElementById('vrbb-summary-freight');
  var freightAmountEl = document.getElementById('vrbb-freight-amount');
  var totalAmountEl = document.getElementById('vrbb-total-amount');
  var summarySizeEl = document.getElementById('vrbb-summary-size');
  var standardEl = document.getElementById('vrbb-s2-standard-price');
  var tradeEl = document.getElementById('vrbb-s2-trade-price');
  var ackWrap = document.getElementById('vrbb-ack-wrap');
  var ackBox = document.getElementById('vrbb-ack');
  var orderBtn = document.getElementById('vrbb-s2-order');
  var orderForm = document.getElementById('vrbb-order-form');
  var orderErrorEl = document.getElementById('vrbb-order-error');

  if (!widthFtSel || !lengthFtSel) return;

  var EMPTY_TEXT = emptyEl ? emptyEl.textContent : '';
  var current = null;
  var pendingMatch = null;

  // ---------- dropdowns ----------
  function addOption(sel, value, label) {
    var o = document.createElement('option');
    o.value = value;
    o.textContent = label;
    sel.appendChild(o);
    return o;
  }

  for (var w = 2; w <= maxWidthFt; w++) addOption(widthFtSel, w, w + ' ft');
  addOption(widthFtSel, OVER, 'Wider than ' + formatCeiling(maxWidthIn) + '\u2026');

  function rebuildLength() {
    var selW = parseInt(widthFtSel.value, 10) || 2;
    var minL = Math.max(selW, 4);
    var keep = lengthFtSel.value;
    lengthFtSel.innerHTML = '';
    addOption(lengthFtSel, '', 'ft');
    for (var l = minL; l <= maxLengthFt; l++) {
      var op = addOption(lengthFtSel, l, l + ' ft');
      if (String(l) === keep) op.selected = true;
    }
    var over = addOption(lengthFtSel, OVER, 'Longer than ' + formatCeiling(maxLengthIn) + '\u2026');
    if (keep === OVER) over.selected = true;
  }

  // The inch list depends on the selected foot. At the top foot it stops at the
  // remainder of the ceiling; below it, the full 0-11 is safe.
  function rebuildInches(inSel, ftSel, ceilingIn) {
    if (!inSel) return;
    var topFt = Math.floor(ceilingIn / 12);
    var selFt = parseInt(ftSel.value, 10);
    var cap = (selFt === topFt) ? ceilingIn % 12 : 11;
    var keep = parseInt(inSel.value, 10) || 0;
    inSel.innerHTML = '';
    for (var i = 0; i <= cap; i++) addOption(inSel, i, i + ' in');
    inSel.value = String(Math.min(keep, cap));
  }
  function rebuildAllInches() {
    rebuildInches(widthInSel, widthFtSel, maxWidthIn);
    rebuildInches(lengthInSel, lengthFtSel, maxLengthIn);
  }

  // ---------- the oversize path ----------
  function openQuoteForm() {
    var trigger = document.querySelector('.vrbb-standalone [data-target="vrbb-quote-form"]');
    var target = document.getElementById('vrbb-quote-form');
    if (trigger && target && target.hidden) trigger.click();
  }

  // Shanna's wording. The same sentence for width and length, and it states no
  // dimension — the ceiling is already in the dropdown option just chosen.
  var OVERSIZE_TEXT = 'Even larger sizes are available, but we price those separately. ' +
    'Send your size request below and we will email you a price within one business day.';

  function showOversize() {
    resetPanels();
    if (emptyEl) {
      emptyEl.textContent = OVERSIZE_TEXT;
      show(emptyEl);
    }
    openQuoteForm();
  }

  // ---------- panels ----------
  function showOrderError(msg) {
    if (!orderErrorEl) { console.error('VRBB order error:', msg); return; }
    orderErrorEl.textContent = msg;
    show(orderErrorEl);
  }
  function clearOrderError() {
    if (orderErrorEl) { orderErrorEl.textContent = ''; hide(orderErrorEl); }
  }

  function resetPanels() {
    clearOrderError();
    hide(guardEl);
    hide(summaryEl);
    hide(freightWrap);
    show(shipEl);
    hide(ackWrap);
    hide(orderBtn);
    hide(orderForm);
    if (orderBtn) {
      orderBtn.textContent = ORDER_LABEL;
      orderBtn.disabled = true;
    }
    current = null;
    pendingMatch = null;
  }

  function showEmpty() {
    resetPanels();
    if (emptyEl) {
      emptyEl.textContent = EMPTY_TEXT;
      show(emptyEl);
    }
  }

  function syncOrderButton() {
    if (!orderBtn || !current) return;
    var ok = !ackBox || ackBox.checked;
    orderBtn.disabled = !ok;
    // The number on the button is the number that gets charged, freight included.
    orderBtn.textContent = ORDER_LABEL + ' \u2014 ' + formatPrice(current.total);
  }

  function showGuard(match) {
    resetPanels();
    hide(emptyEl);
    pendingMatch = match;
    show(guardEl);
  }

  function showPrice(widthIn, lengthIn) {
    resetPanels();
    hide(emptyEl);

    var rate = rateAt(widthIn);
    var freight = freightAt(widthIn);

    // A width with no band, or a rate with no freight answer, is a broken table
    // rather than a free rug. Route to the quote form instead of guessing.
    if (rate === null || freight === null) { showOversize(); return; }

    var base = calculatePrice(rate, widthIn, lengthIn);
    var shown = isTrade ? tradePrice(base, tradeTier) : base;
    var total = shown + freight; // freight is never discounted

    if (summarySizeEl) {
      summarySizeEl.textContent = formatDimension(widthIn) + ' \u00d7 ' + formatDimension(lengthIn);
    }
    if (isTrade) {
      if (standardEl) standardEl.textContent = formatPrice(base);
      if (tradeEl) tradeEl.textContent = formatPrice(shown);
    } else if (standardEl) {
      standardEl.textContent = formatPrice(base);
    }

    // One line or the other, never both: free shipping, or a freight charge
    // with a total. A total is only shown when there is something to add up.
    if (freight > 0) {
      if (freightAmountEl) freightAmountEl.textContent = formatPrice(freight);
      if (totalAmountEl) totalAmountEl.textContent = formatPrice(total);
      hide(shipEl);
      show(freightWrap);
    } else {
      hide(freightWrap);
      show(shipEl);
    }

    current = {
      widthIn: widthIn,
      lengthIn: lengthIn,
      base: base,
      shown: shown,
      freight: freight,
      total: total
    };

    show(summaryEl);
    show(ackWrap);
    show(orderBtn);
    if (ackBox) ackBox.checked = false;
    syncOrderButton();
  }

  // ---------- main ----------
  function update() {
    var wFt = widthFtSel.value;
    var lFt = lengthFtSel.value;

    if (wFt === OVER || lFt === OVER) { showOversize(); return; }
    if (!wFt || !lFt) { showEmpty(); return; }

    var widthIn = toInches(parseInt(wFt, 10), parseInt(widthInSel.value, 10));
    var lengthIn = toInches(parseInt(lFt, 10), parseInt(lengthInSel.value, 10));

    // Belt and braces: rebuildInches should make these unreachable. The width
    // test is now redundant with the band lookup in showPrice, and kept because
    // it fails fast and costs nothing.
    if (widthIn > maxWidthIn || lengthIn > maxLengthIn) { showOversize(); return; }

    var match = matchInStock(widthIn, lengthIn);
    if (match) { showGuard(match); return; }
    showPrice(widthIn, lengthIn);
  }

  rebuildLength();
  rebuildAllInches();
  widthFtSel.addEventListener('change', function () { rebuildLength(); rebuildAllInches(); update(); });
  widthInSel.addEventListener('change', update);
  lengthFtSel.addEventListener('change', function () { rebuildAllInches(); update(); });
  lengthInSel.addEventListener('change', update);
  if (ackBox) ackBox.addEventListener('change', syncOrderButton);

  // ---------- switch to the stocked size ----------
  // Done on the page: flip to Ready to ship, then set the size in Dawn's picker
  // so it updates price and Add to cart itself. Navigation is the fallback only.
  function labelsMatch(a, b) {
    if (!a || !b) return false;
    return String(a).replace(/\s+/g, '').toLowerCase() === String(b).replace(/\s+/g, '').toLowerCase();
  }
  function selectStockedVariant(m) {
    var scope = document.querySelector('.product__info-container') || document;
    var radios = scope.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      if (labelsMatch(radios[i].value, m.o1) || labelsMatch(radios[i].value, m.t)) {
        if (!radios[i].checked) {
          radios[i].checked = true;
          radios[i].dispatchEvent(new Event('change', { bubbles: true }));
        }
        return true;
      }
    }
    var sels = scope.querySelectorAll('select');
    for (var j = 0; j < sels.length; j++) {
      for (var k = 0; k < sels[j].options.length; k++) {
        var opt = sels[j].options[k];
        if (labelsMatch(opt.value, m.o1) || labelsMatch(opt.value, m.t)) {
          sels[j].value = opt.value;
          sels[j].dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
    }
    return false;
  }

  if (guardSwitchBtn) {
    guardSwitchBtn.addEventListener('click', function () {
      if (!pendingMatch) return;
      var stockTab = document.getElementById('vrbb-path-stock');
      if (stockTab) stockTab.click();
      if (selectStockedVariant(pendingMatch)) return;
      if (stockTab) stockTab.click();
      guardSwitchBtn.disabled = true;
      guardSwitchBtn.textContent = 'Switching\u2026';
      window.location.href = pendingMatch.u;
    });
  }

  // ---------- shortcuts ----------
  Array.prototype.forEach.call(document.querySelectorAll('.vrbb-shortcut'), function (btn) {
    btn.addEventListener('click', function () {
      widthFtSel.value = parseInt(btn.dataset.width, 10);
      rebuildLength();
      lengthFtSel.value = parseInt(btn.dataset.length, 10);
      rebuildAllInches();
      widthInSel.value = 0;
      lengthInSel.value = 0;
      update();
    });
  });

  // ---------- order ----------
  function openCustomOrderForm() {
    if (!current) return;
    var wDisp = formatDimension(current.widthIn);
    var lDisp = formatDimension(current.lengthIn);
    var priceStr = formatPrice(current.total);

    setText('vrbb-order-width-display', wDisp);
    setText('vrbb-order-length-display', lDisp);
    setText('vrbb-order-price-display', priceStr);
    setVal('vrbb-order-width-hidden', wDisp);
    setVal('vrbb-order-length-hidden', lDisp);
    setVal('vrbb-order-price-hidden', priceStr);

    show(orderForm);
  }

  // Price and trade tier are NOT sent. The proxy re-derives both server-side
  // from the vendor and the dimensions; anything sent from here would be a
  // second source of truth for the number that gets charged.
  function submitDraftOrder() {
    if (!current) return;
    clearOrderError();
    orderBtn.textContent = 'Processing\u2026';
    orderBtn.disabled = true;
    if (window.vrbbShowOverlay) window.vrbbShowOverlay();

    fetch(DRAFT_ORDER_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product_id: dataEl.dataset.productId || null,
        vendor: dataEl.dataset.vendor || null,
        dimensions: {
          width_ft: parseInt(widthFtSel.value, 10),
          width_in: parseInt(widthInSel.value, 10),
          length_ft: parseInt(lengthFtSel.value, 10),
          length_in: parseInt(lengthInSel.value, 10)
        }
      })
    })
      .then(function (r) {
        return r.text().then(function (body) {
          var data = null;
          try { data = JSON.parse(body); } catch (e) {}
          return { ok: r.ok, status: r.status, data: data };
        });
      })
      .then(function (res) {
        if (res.ok && res.data && res.data.invoice_url) {
          window.location.href = res.data.invoice_url;
          return;
        }
        if (window.vrbbHideOverlay) window.vrbbHideOverlay();
        syncOrderButton();
        // The proxy returns readable messages. Show them — silent failure is
        // what made this hard to find.
        var msg = res.data && (res.data.error || res.data.message);
        showOrderError(msg || 'We couldn\u2019t start this order. Please try again, or send your size below and Shanna will email you a price.');
        console.error('VRBB draft-order failed', res.status, res.data);
      })
      .catch(function (err) {
        if (window.vrbbHideOverlay) window.vrbbHideOverlay();
        syncOrderButton();
        showOrderError('We couldn\u2019t reach the order system just now. Please try again, or send your size below and Shanna will email you a price.');
        console.error('VRBB draft-order error:', err);
      });
  }

  if (orderBtn) {
    orderBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (!current || orderBtn.disabled) return;
      if (CUSTOM_CHECKOUT_ENABLED) submitDraftOrder();
      else openCustomOrderForm();
    });
  }

  // ---------- back-button recovery ----------
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) update();
  });

  var orderFormEl = document.getElementById('vrbb-order-form-el');
  if (orderFormEl) {
    orderFormEl.addEventListener('submit', function () {
      try { window.sessionStorage.setItem('vrbb_order_submitted', '1'); } catch (e) {}
    });
  }

  // ---------- deep-link prefill ----------
  // A feed link naming a size that isn't on the shelf. The tab is already open;
  // filling the size in means the shopper isn't asked to retype what they
  // clicked. The Liquid only emits these when the selected variant has no
  // stock, so the in-stock guard cannot fire on a prefill. Anything that does
  // not fit this rug's own dropdowns is left alone rather than forced.
  function applyPrefill() {
    var pw = parseFloat(dataEl.dataset.prefillWidthFt);
    var pl = parseFloat(dataEl.dataset.prefillLengthFt);
    if (!(pw > 0) || !(pl > 0)) return false;

    var wIn = Math.round(pw * 12);
    var lIn = Math.round(pl * 12);
    if (wIn > maxWidthIn || lIn > maxLengthIn) return false;

    var wFt = Math.floor(wIn / 12);
    var lFt = Math.floor(lIn / 12);

    widthFtSel.value = String(wFt);
    if (widthFtSel.value !== String(wFt)) return false;

    rebuildLength();
    lengthFtSel.value = String(lFt);
    if (lengthFtSel.value !== String(lFt)) return false;

    rebuildAllInches();
    if (widthInSel) widthInSel.value = String(wIn % 12);
    if (lengthInSel) lengthInSel.value = String(lIn % 12);

    update();
    return true;
  }

  if (!applyPrefill()) showEmpty();
})();
